/**
 * ReviewerPromptAssistant Component
 *
 * AI assistant panel for generating and refining therapy reviewer personas.
 * Supports conversational refinement with provider/model selection.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  Alert
} from '@mui/material';
import {
  AutoAwesome as GenerateIcon,
  Refresh as ResetIcon,
  CheckCircle as ApplyIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { generateReviewerPersona, getAvailableLLMModels, getLLMConfiguration } from '../../../services/api';

const LLM_PROVIDERS = [
  { value: 'vertex_ai', label: 'Vertex AI (Google)' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'gemini', label: 'Gemini' }
];

const DEFAULT_LLM_MODELS = {
  vertex_ai: ['gemini-2.5-flash', 'gemini-2.5-pro', 'gemini-1.5-flash'],
  openai: ['gpt-4o', 'gpt-4-turbo'],
  gemini: ['gemini-2.5-flash', 'gemini-2.5-pro']
};

function ReviewerPromptAssistant({ currentPersona, onApply, token }) {
  const [provider, setProvider] = useState('vertex_ai');
  const [model, setModel] = useState('gemini-2.5-flash');
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [lastGenerated, setLastGenerated] = useState(null);
  const [availableModels, setAvailableModels] = useState({});
  const [loadingModels, setLoadingModels] = useState(true);
  const chatEndRef = useRef(null);

  // Load available LLM models
  useEffect(() => {
    const loadModels = async () => {
      try {
        setLoadingModels(true);
        const [modelsRes, configRes] = await Promise.all([
          getAvailableLLMModels(token, { activeOnly: true }),
          getLLMConfiguration(token)
        ]);

        const grouped = { openai: [], gemini: [], vertex_ai: [] };
        (modelsRes.models || []).forEach(m => {
          if (grouped[m.provider]) {
            grouped[m.provider].push(m);
          }
        });
        setAvailableModels(grouped);

        if (configRes.configuration) {
          setProvider(configRes.configuration.primaryProvider || 'vertex_ai');
          setModel(configRes.configuration.primaryModel || 'gemini-2.5-flash');
        }
      } catch (err) {
        console.error('Failed to load LLM models:', err);
      } finally {
        setLoadingModels(false);
      }
    };
    loadModels();
  }, [token]);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversationHistory]);

  const getModelsForProvider = (prov) => {
    const models = availableModels[prov];
    if (models && models.length > 0) return models;
    return (DEFAULT_LLM_MODELS[prov] || []).map(id => ({ id, displayName: id }));
  };

  const handleProviderChange = (newProvider) => {
    setProvider(newProvider);
    const models = getModelsForProvider(newProvider);
    if (models.length > 0) {
      setModel(models[0].id || models[0]);
    }
  };

  const handleGenerate = async () => {
    if (!userInput.trim()) return;

    setLoading(true);
    setError(null);

    const userMessage = userInput.trim();
    const newHistory = [...conversationHistory, { role: 'user', content: userMessage }];
    setConversationHistory(newHistory);
    setUserInput('');

    try {
      // Build the current persona context from form state if available
      const personaContext = currentPersona ? {
        name: currentPersona.name,
        key: currentPersona.key,
        description: currentPersona.description,
        system_prompt: currentPersona.system_prompt,
        reviewConfiguration: currentPersona.reviewConfiguration
      } : null;

      const result = await generateReviewerPersona({
        prompt: userMessage,
        provider,
        model,
        temperature: 0.4,
        currentPersona: personaContext,
        conversationHistory: conversationHistory // Send prior messages for context
      }, token);

      if (result.mode === 'feedback') {
        // Feedback mode — no persona to apply, just show the analysis
        let feedbackText = result.feedback || 'No feedback provided.';
        if (result.suggestedChanges && result.suggestedChanges.length > 0) {
          feedbackText += '\n\nSuggested changes:\n' + result.suggestedChanges.map(c => `- ${c}`).join('\n');
        }
        setConversationHistory([...newHistory, { role: 'assistant', content: feedbackText }]);
      } else {
        // Generate mode — persona available to apply
        const generated = result.generated;
        setLastGenerated(generated);

        // Show the AI's summary of what it created/changed
        let summary = result.changesSummary || '';
        if (!summary) {
          const dims = generated.reviewConfiguration?.evaluationDimensions || [];
          summary = `Generated "${generated.name}" with ${dims.length} dimensions.`;
        }
        summary += '\n\nClick "Apply to Form" to populate the editor with these changes.';

        setConversationHistory([...newHistory, { role: 'assistant', content: summary }]);
      }

    } catch (err) {
      setError(err.message || 'Generation failed');
      // Remove the user message if it failed
      setConversationHistory(conversationHistory);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (lastGenerated && onApply) {
      onApply(lastGenerated);
    }
  };

  const handleReset = () => {
    setConversationHistory([]);
    setLastGenerated(null);
    setError(null);
    setUserInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleGenerate();
    }
  };

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'grey.50',
        borderRadius: 2
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GenerateIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1" fontWeight="bold">AI Prompt Assistant</Typography>
        </Box>
        {conversationHistory.length > 0 && (
          <Tooltip title="Reset conversation">
            <IconButton size="small" onClick={handleReset}>
              <ResetIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Describe the reviewer you want and the AI will generate the complete persona configuration.
      </Typography>

      {/* Provider / Model selectors */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <FormControl size="small" sx={{ flex: 1 }}>
          <InputLabel>Provider</InputLabel>
          <Select
            value={provider}
            onChange={(e) => handleProviderChange(e.target.value)}
            label="Provider"
            disabled={loading}
          >
            {LLM_PROVIDERS.map(p => (
              <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ flex: 1 }}>
          <InputLabel>Model</InputLabel>
          <Select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            label="Model"
            disabled={loading || loadingModels}
          >
            {getModelsForProvider(provider).map(m => {
              const id = m.id || m;
              const display = m.displayName || id;
              return (
                <MenuItem key={id} value={id}>
                  {display}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Box>

      {/* Conversation History */}
      {conversationHistory.length > 0 && (
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            mb: 2,
            maxHeight: 280,
            minHeight: 100,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1,
            bgcolor: 'white'
          }}
        >
          {conversationHistory.map((msg, idx) => (
            <Box
              key={idx}
              sx={{
                mb: 1,
                p: 1,
                borderRadius: 1,
                bgcolor: msg.role === 'user' ? 'primary.50' : 'success.50',
                borderLeft: '3px solid',
                borderColor: msg.role === 'user' ? 'primary.main' : 'success.main'
              }}
            >
              <Typography variant="caption" color="text.secondary" fontWeight="bold">
                {msg.role === 'user' ? 'You' : 'AI'}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {msg.content}
              </Typography>
            </Box>
          ))}
          <div ref={chatEndRef} />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Input area */}
      <TextField
        fullWidth
        multiline
        rows={3}
        size="small"
        placeholder={
          conversationHistory.length === 0
            ? 'e.g., "Create a DBT reviewer focusing on dialectical balance, validation levels 1-6, distress tolerance skills, and behaviour chain analysis. Use a 0-5 scoring scale."'
            : 'e.g., "Add more focus on crisis intervention" or "Change the scoring scale to 0-7"'
        }
        value={userInput}
        onChange={(e) => setUserInput(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={loading}
        sx={{ mb: 1 }}
      />

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
        <Button
          variant="contained"
          size="small"
          onClick={handleGenerate}
          disabled={loading || !userInput.trim()}
          startIcon={loading ? <CircularProgress size={16} /> : <SendIcon />}
        >
          {loading ? 'Thinking...' : 'Send'}
        </Button>

        {lastGenerated && (
          <Button
            variant="outlined"
            size="small"
            color="success"
            onClick={handleApply}
            startIcon={<ApplyIcon />}
          >
            Apply to Form
          </Button>
        )}
      </Box>
    </Paper>
  );
}

export default ReviewerPromptAssistant;
